import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../redux/store";
import axiosInstance from "../services/axiosInstance";
import { AuthState, getCurrentUser } from "../redux/slices/authSlice";
import { AuthService } from "../services/authService";
import Loader from "../components/common/Loader";

type AuthGuardProps = {
  children: React.ReactNode;
};

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const isRefreshing = useRef<boolean>(false);
  const failedQueue = useRef<Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }>>([]);

  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser, loading } = useSelector<unknown, AuthState>(
    (state: any) => state.auth
  );

  const processQueue = (error: unknown = null) => {
    failedQueue.current.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    failedQueue.current = [];
  };

  const forceLogout = () => {
    sessionStorage.setItem("session_expired", "true");
    navigate("/auth", { replace: true });
    window.location.reload();
  };

  useEffect(() => {
    const axiosInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/refresh')
        ) {
          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            })
              .then(() => axiosInstance(originalRequest))
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing.current = true;

          try {
            const response = await AuthService.refreshToken();

            if (response && response.status !== -1) {
              processQueue();
              isRefreshing.current = false;
              return axiosInstance(originalRequest);
            } else {
              processQueue(error);
              isRefreshing.current = false;
              forceLogout();
              return Promise.reject(error);
            }
          } catch (refreshError: any) {
            processQueue(refreshError);
            isRefreshing.current = false;
            forceLogout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    if (!currentUser) {
      dispatch(getCurrentUser());
    }

    return () => {
      axiosInstance.interceptors.response.eject(axiosInterceptor);
    };
  }, [currentUser, dispatch, navigate]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><Loader/></div>;
  }

  return <div className="w-full">{children}</div>;
};

export default AuthGuard;
